import { Link } from "react-router-dom";
import { CheckCircle2, ArrowRight } from "lucide-react";
import { getServices } from "@/data/services";
import { GALLERY } from "@/data/site";
import { getGeneralFaqs } from "@/data/faqs";
import { useLang } from "@/lib/i18n";

import Hero from "@/components/site/Hero";
import ServiceCard from "@/components/site/ServiceCard";
import Testimonials from "@/components/site/Testimonials";
import Gallery from "@/components/site/Gallery";
import FloridaMap from "@/components/site/ServiceAreaMap";
import MapSideCTA from "@/components/site/MapSideCTA";
import PromoStrip from "@/components/site/PromoStrip";
import FAQ from "@/components/site/FAQ";
import ReadyCTA from "@/components/site/ReadyCTA";
import BeforeAfter from "@/components/site/BeforeAfter";

export default function Home() {
  const { t, lang } = useLang();
  const SERVICES = getServices(lang);
  const featured = SERVICES.filter((s) => s.homeFeatured).slice(0, 6);
  const FAQS = getGeneralFaqs(lang);

  return (
    <>
      <Hero />
      <PromoStrip />

      {/* Featured Services (6) */}
      <section
        data-testid="home-services"
        className="section-padding bg-[linear-gradient(180deg,#FAFAF7_0%,#F8FAFC_100%)] relative"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/40 to-transparent" />
        <div className="container-max">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
                {t.home.whatEyebrow}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 max-w-2xl leading-tight">
                {t.home.whatTitle}
              </h2>
              <p className="text-slate-600 mt-3 max-w-2xl">{t.home.whatSub}</p>
            </div>
            <Link
              to="/services"
              data-testid="home-services-all"
              className="inline-flex items-center gap-1.5 self-start text-sm font-bold text-slate-900 bg-brand-yellow hover:bg-brand-yellow-dark transition-colors px-4 py-2.5 rounded-xl whitespace-nowrap"
            >
              {t.cta.viewAll}
              <ArrowRight size={16} />
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {featured.map((s) => (
              <ServiceCard key={s.slug} service={s} />
            ))}
          </div>
        </div>
      </section>

      {/* Why Faster — dark band, no overlay glare */}
      <section
        data-testid="home-why"
        className="section-padding relative overflow-hidden bg-brand-navy-dark text-white"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow to-transparent" />
        <div className="absolute inset-x-0 bottom-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/40 to-transparent" />
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-brand-navy-light/15 blur-3xl pointer-events-none" />
        <div className="absolute -bottom-32 -left-32 w-[380px] h-[380px] rounded-full bg-brand-yellow/10 blur-3xl pointer-events-none" />

        <div className="relative container-max grid lg:grid-cols-2 gap-12 items-center">
          <div className="relative">
            <div className="aspect-[5/4] rounded-3xl overflow-hidden border-4 border-white/15 ring-1 ring-brand-yellow/20 shadow-2xl">
              <img
                src={GALLERY[1].hero}
                alt={GALLERY[1].alt}
                className="w-full h-full object-cover"
                loading="lazy"
              />
            </div>
            <div className="absolute -bottom-6 -right-6 bg-brand-yellow text-slate-900 rounded-2xl p-5 shadow-xl hidden sm:block">
              <p className="font-heading text-3xl font-bold">12 {lang === "es" ? "años" : "yrs"}</p>
              <p className="text-xs uppercase tracking-wider mt-1 font-semibold">
                {lang === "es" ? "sirviendo FL" : "serving FL"}
              </p>
            </div>
          </div>
          <div>
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow">
              {t.home.whyEyebrow}
            </p>
            <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold mt-2 leading-tight">
              {t.home.whyTitle}
            </h2>
            <p className="mt-5 text-white/75 text-lg">{t.home.whySub}</p>

            <ul className="mt-7 space-y-3.5">
              {t.home.whyBullets.map((line) => (
                <li key={line} className="flex items-start gap-3">
                  <CheckCircle2 size={20} className="text-brand-yellow shrink-0 mt-0.5" />
                  <span className="text-white/85">{line}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Gallery />

      <BeforeAfter />

      {/* Map + Side CTA — colored radial section */}
      <section
        data-testid="home-area-map"
        className="section-padding relative bg-[radial-gradient(ellipse_at_top,#FFFBEB_0%,#F8FAFC_55%,#EFF6FF_100%)]"
      >
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-brand-yellow/50 to-transparent" />
        <div className="container-max">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-10">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-brand-yellow-dark">
                {t.home.whereEyebrow}
              </p>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mt-2 leading-tight max-w-2xl">
                {t.home.whereTitle}
              </h2>
              <p className="text-slate-600 mt-3 max-w-2xl">{t.home.whereSub}</p>
            </div>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <FloridaMap />
            <MapSideCTA />
          </div>
        </div>
      </section>

      <Testimonials />

      <FAQ items={FAQS} />

      <ReadyCTA />
    </>
  );
}
